import React from 'react';
import classnames from 'classnames/bind';
import { Pagination } from '@mantine/core';

import { Category } from 'types/category';
import { PipelineTemplateSummary } from 'types/pipeline_template';

import Pipeline from 'types/pipeline';

import { useAppRedirect } from 'hooks/useAppRedirect';
import { useCreatePipelineFromTemplate } from 'pipelines/hooks/useCreatePipelineFromTemplate';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import {
  UsePipelineTemplatesParams,
  usePipelineTemplates,
} from 'pipelines/hooks/usePipelineTemplates';

import { EmptyView } from 'components/EmptyView/EmptyView';
import {
  PipelineTemplateCard,
  PipelineTemplateCardSkeleton,
} from 'analytics-library/components/PipelineTemplateCard/PipelineTemplateCard';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { TableManager } from 'components/Table/Table';

import EmptyIllustration from 'analytics-library/images/empty-models-illustration.svg?react';
import styles from './PipelineTemplateGrid.module.scss';

const c = classnames.bind(styles);

export type PipelineTemplateGridContentProps = {
  categories: Category[];
  filters?: Omit<UsePipelineTemplatesParams, 'category_labels'> & {
    limit?: number;
  };
  onlyIncludedInPlan?: boolean;
  page: number;
  onTotalElementsChange?: (totalElements: number | undefined) => void;
  onPageChange: (page: number) => void;
  onCreatePipeline?: () => void;
  onDeployPipeline?: (pipeline: Pipeline) => void;
};

export function PipelineTemplateGridContent({
  page,
  onPageChange,
  categories,
  filters,
  onlyIncludedInPlan = true,
  onTotalElementsChange,
  onCreatePipeline,
  onDeployPipeline,
}: PipelineTemplateGridContentProps) {
  const { isSimplifiedUxPlan } = useCurrentPlan();

  const { data, isLoading, isPreviousData } = usePipelineTemplates({
    params: {
      ...filters,
      page,
      category_labels: categories.map(({ label }) => label),
      only_included_in_plan: onlyIncludedInPlan,
    },
    keepPreviousData: true,
  });

  React.useEffect(() => {
    onTotalElementsChange?.(data?.total_elements);
  }, [data?.total_elements, onTotalElementsChange]);

  const hasFilters =
    categories.length > 0 || Boolean(filters?.query && filters.query.trim());

  const redirect = useAppRedirect();

  const {
    isLoading: isCreating,
    mutate: createPipelineFromTemplate,
    variables: createVariables,
  } = useCreatePipelineFromTemplate({
    onSuccess(pipeline) {
      redirect(`/design/pipelines/${pipeline.id}`);
      onCreatePipeline?.();
    },
  });

  const {
    isLoading: isCreatingForDeploy,
    mutate: createPipelineForDeploy,
    variables: deployVariables,
  } = useCreatePipelineFromTemplate({
    onSuccess(pipeline) {
      onDeployPipeline?.(pipeline);
    },
  });

  function handleCreatePipelineFromTemplate(template: PipelineTemplateSummary) {
    createPipelineFromTemplate({ template });
  }

  function handleDeployPipelineFromTemplate(template: PipelineTemplateSummary) {
    createPipelineForDeploy({ template });
  }

  if (isLoading) {
    return (
      <div className={c('template-grid')}>
        <SkeletonList
          component={<PipelineTemplateCardSkeleton />}
          min={7}
          max={22}
        />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className={c('empty')}>
        <EmptyView
          image={EmptyIllustration}
          title={
            hasFilters
              ? 'No solution templates matching your search input found.'
              : 'No solution templates available.'
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className={c('template-grid')}>
        {isLoading || isPreviousData ? (
          <SkeletonList
            component={<PipelineTemplateCardSkeleton />}
            min={7}
            max={22}
          />
        ) : (
          data.data.map((template) => (
            <PipelineTemplateCard
              template={template}
              onClick={handleCreatePipelineFromTemplate}
              onDeploy={
                onDeployPipeline ? handleDeployPipelineFromTemplate : undefined
              }
              isLoading={
                (createVariables?.template.id === template.id && isCreating) ||
                (deployVariables?.template.id === template.id &&
                  isCreatingForDeploy)
              }
              key={template.id}
              showCreateButton={!isSimplifiedUxPlan}
              showDeployButton={!!onDeployPipeline}
            />
          ))
        )}
      </div>

      <TableManager>
        <Pagination
          total={data.total_pages}
          value={page}
          onChange={onPageChange}
        />
      </TableManager>
    </>
  );
}
